/**
 * 画布通用工具
 */
import type { CanvasNode, CanvasNodeType } from './types';
import { DEFAULT_NODE_SIZES } from './constants';

export function nid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 哨兵错误 message：生成中的占位节点被撤销/删除时抛出，
 * 用于中断轮询并告知 catch 静默跳过（节点已不存在，无需写回状态）。
 */
export const CANVAS_NODE_REMOVED = '__canvas_node_removed__';

export function makeNode<T extends object = Record<string, unknown>>(
  type: CanvasNodeType,
  pos: { x: number; y: number },
  data: T,
  size?: { width: number; height: number },
  zIndex = 0
): CanvasNode {
  const fallback = DEFAULT_NODE_SIZES[type] || { width: 320, height: 220 };
  return {
    id: nid(),
    type,
    x: pos.x,
    y: pos.y,
    width: size?.width ?? fallback.width,
    height: size?.height ?? fallback.height,
    zIndex,
    data: data as unknown as CanvasNode['data'],
  };
}

/**
 * 屏幕坐标 → 画布坐标
 */
export function screenToCanvas(
  screenX: number,
  screenY: number,
  viewport: { x: number; y: number; zoom: number },
  containerRect: DOMRect
) {
  return {
    x: (screenX - containerRect.left - viewport.x) / viewport.zoom,
    y: (screenY - containerRect.top - viewport.y) / viewport.zoom,
  };
}

/**
 * 判断任意节点中心是否落在分组矩形内（分组不嵌套）。
 * 放在 utils 以避免 boardStore ↔ GroupNode 循环依赖。
 */
export function isNodeInsideGroup(node: CanvasNode, group: CanvasNode): boolean {
  if (node.type === 'group') return false;
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;
  return (
    cx >= group.x &&
    cx <= group.x + group.width &&
    cy >= group.y &&
    cy <= group.y + group.height
  );
}

/**
 * 画布坐标 → 屏幕坐标（含容器偏移）
 */
export function canvasToScreen(
  canvasX: number,
  canvasY: number,
  viewport: { x: number; y: number; zoom: number },
  containerRect: DOMRect
) {
  return {
    x: canvasX * viewport.zoom + viewport.x + containerRect.left,
    y: canvasY * viewport.zoom + viewport.y + containerRect.top,
  };
}
